import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Mail,
  Check,
  X,
  Clock,
  Building2,
  User,
  AlertCircle,
  Loader2,
  ChevronRight,
  Home,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  useInvitationStore,
  usePendingInvitations,
  usePendingCount,
  type PendingInvitation,
} from "@/stores/invitation/invitation-store";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import {
  ERROR,
  FOREST_GREEN,
  TEXT,
  BACKGROUND,
  MID_GRAY,
} from "@/styles/tokens";
import { getInvitationAcceptRedirect } from "@/utils/invitationRedirect";

// Maximum items to show in dropdown
const MAX_DROPDOWN_ITEMS = 5;

interface PendingInvitationsDropdownProps {
  className?: string;
}

/**
 * PendingInvitationsDropdown
 *
 * A dropdown menu that displays pending organization invitations
 * with accept/decline actions for each invitation.
 */
export const PendingInvitationsDropdown: React.FC<
  PendingInvitationsDropdownProps
> = ({ className }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const pendingInvitations = usePendingInvitations();
  const pendingCount = usePendingCount();
  const isLoading = useInvitationStore((state) => state.isLoading);
  const error = useInvitationStore((state) => state.error);
  const fetchPendingInvitations = useInvitationStore(
    (state) => state.fetchPendingInvitations
  );
  const acceptInvitation = useInvitationStore(
    (state) => state.acceptInvitation
  );
  const declineInvitation = useInvitationStore(
    (state) => state.declineInvitation
  );
  const clearError = useInvitationStore((state) => state.clearError);

  // Fetch organization store actions
  const fetchOrganizations = useOrganizationStore(
    (state) => state.fetchOrganizations
  );

  // Fetch invitations when dropdown opens
  useEffect(() => {
    if (open) {
      fetchPendingInvitations();
    }
  }, [open, fetchPendingInvitations]);

  // Handle accept invitation
  const handleAccept = async (invitation: PendingInvitation) => {
    setProcessingId(invitation.membershipId);
    clearError();

    try {
      const result = await acceptInvitation(invitation.membershipId);

      toast.success(`Successfully joined ${result.organization?.name ?? invitation.organization.name}!`, {
        description: `You are now a ${invitation.role} of the organization.`,
      });

      // Refresh organizations list
      await fetchOrganizations();

      navigate(getInvitationAcceptRedirect(result));
      setOpen(false);
    } catch (err) {
      console.error("[PendingInvitationsDropdown] Error accepting invitation:", err);
      toast.error("Failed to accept invitation", {
        description: err instanceof Error ? err.message : "Please try again.",
      });
    } finally {
      setProcessingId(null);
    }
  };

  // Handle decline invitation
  const handleDecline = async (invitation: PendingInvitation) => {
    setProcessingId(invitation.membershipId);
    clearError();

    try {
      await declineInvitation(invitation.membershipId);

      toast.success("Invitation declined", {
        description: `You have declined the invitation to ${invitation.organization.name}.`,
      });
    } catch (err) {
      console.error("[PendingInvitationsDropdown] Error declining invitation:", err);
      toast.error("Failed to decline invitation", {
        description: err instanceof Error ? err.message : "Please try again.",
      });
    } finally {
      setProcessingId(null);
    }
  };

  // Format expiry date
  const formatExpiry = (expiresAt: string): string => {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diffMs = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) {
      return "Expired";
    } else if (diffDays === 1) {
      return "Expires tomorrow";
    } else if (diffDays <= 7) {
      return `Expires in ${diffDays} days`;
    } else {
      return `Expires ${expiry.toLocaleDateString()}`;
    }
  };

  // Check if invitation is expiring soon (within 24 hours)
  const isExpiringSoon = (expiresAt: string): boolean => {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diffMs = expiry.getTime() - now.getTime();
    return diffMs > 0 && diffMs < 24 * 60 * 60 * 1000;
  };

  // Get role badge variant
  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case "admin":
        return "default";
      case "member":
        return "secondary";
      case "viewer":
        return "outline";
      default:
        return "outline";
    }
  };

  // Display count for badge
  const displayCount = pendingCount > 99 ? "99+" : pendingCount;

  // Invitations to show (limited for dropdown)
  const displayedInvitations = pendingInvitations.slice(0, MAX_DROPDOWN_ITEMS);
  const hasMore = pendingInvitations.length > MAX_DROPDOWN_ITEMS;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            "relative p-2 hover:bg-accent rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring",
            className
          )}
          aria-label={
            pendingCount > 0
              ? `${pendingCount} pending invitation${pendingCount !== 1 ? "s" : ""}`
              : "No pending invitations"
          }
        >
          <Mail className="h-5 w-5" aria-hidden="true" />

          {/* Notification badge */}
          {pendingCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white animate-in zoom-in-50 duration-200"
              style={{ backgroundColor: ERROR.DEFAULT }}
              aria-hidden="true"
            >
              {displayCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-[380px] max-h-[480px] overflow-hidden"
        sideOffset={8}
      >
        {/* Header */}
        <DropdownMenuLabel className="flex items-center justify-between py-3 px-4">
          <span className="text-base font-semibold">Invitations</span>
          {pendingCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {pendingCount} pending
            </Badge>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="my-0" />

        {/* Content */}
        <div className="overflow-y-auto max-h-[380px]">
          {/* Loading state */}
          {isLoading && pendingInvitations.length === 0 && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">
                Loading invitations...
              </span>
            </div>
          )}

          {/* Error state */}
          {error && !isLoading && (
            <div className="flex flex-col items-center justify-center py-8 px-4">
              <AlertCircle
                className="h-8 w-8 mb-2"
                style={{ color: ERROR.DEFAULT }}
              />
              <p className="text-sm text-center text-muted-foreground mb-3">
                {error}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchPendingInvitations()}
              >
                Try Again
              </Button>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && pendingInvitations.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 px-4">
              <Mail className="h-10 w-10 mb-2 text-muted-foreground opacity-50" />
              <p className="text-sm text-muted-foreground text-center">
                No pending invitations
              </p>
              <p className="text-xs text-muted-foreground/70 text-center mt-1">
                When someone invites you to an organization, it will appear here.
              </p>
            </div>
          )}

          {/* Invitation list */}
          {!error && displayedInvitations.length > 0 && (
            <div className="divide-y divide-border">
              {displayedInvitations.map((invitation) => (
                <InvitationCard
                  key={invitation.membershipId}
                  invitation={invitation}
                  onAccept={() => handleAccept(invitation)}
                  onDecline={() => handleDecline(invitation)}
                  isProcessing={processingId === invitation.membershipId}
                  formatExpiry={formatExpiry}
                  isExpiringSoon={isExpiringSoon}
                  getRoleBadgeVariant={getRoleBadgeVariant}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer - View all link */}
        {hasMore && (
          <>
            <DropdownMenuSeparator className="my-0" />
            <div className="p-2">
              <Button
                variant="ghost"
                className="w-full justify-between text-sm"
                onClick={() => {
                  // TODO: Navigate to full invitations page when implemented
                  setOpen(false);
                  toast.info("Full invitations page coming soon!");
                }}
              >
                <span>View all {pendingCount} invitations</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * InvitationCard
 *
 * Individual invitation item within the dropdown.
 */
interface InvitationCardProps {
  invitation: PendingInvitation;
  onAccept: () => void;
  onDecline: () => void;
  isProcessing: boolean;
  formatExpiry: (expiresAt: string) => string;
  isExpiringSoon: (expiresAt: string) => boolean;
  getRoleBadgeVariant: (role: string) => "default" | "secondary" | "outline";
}

const InvitationCard: React.FC<InvitationCardProps> = ({
  invitation,
  onAccept,
  onDecline,
  isProcessing,
  formatExpiry,
  isExpiringSoon,
  getRoleBadgeVariant,
}) => {
  const expiryText = formatExpiry(invitation.expiresAt);
  const expiringSoon = isExpiringSoon(invitation.expiresAt);

  return (
    <div
      className={cn(
        "p-4 hover:bg-accent/50 transition-colors",
        isProcessing && "opacity-60 pointer-events-none"
      )}
    >
      {/* Organization info */}
      <div className="flex items-start gap-3 mb-3">
        {/* Organization avatar */}
        <div
          className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0"
          style={{
            backgroundColor: FOREST_GREEN.faint,
            color: FOREST_GREEN.DEFAULT,
          }}
        >
          <Building2 className="h-5 w-5" />
        </div>

        {/* Organization details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-medium text-sm truncate">
              {invitation.organization.name}
            </h4>
            <Badge
              variant={getRoleBadgeVariant(invitation.role)}
              className="text-[10px] px-1.5 py-0 h-5 capitalize"
            >
              {invitation.role}
            </Badge>
          </div>

          {/* Description */}
          {invitation.organization.description && (
            <p
              className="text-xs line-clamp-1 mt-0.5"
              style={{ color: MID_GRAY.DEFAULT }}
            >
              {invitation.organization.description}
            </p>
          )}

          {/* Inviter */}
          <div
            className="flex items-center gap-1 mt-1 text-xs"
            style={{ color: MID_GRAY.DEFAULT }}
          >
            <User className="h-3 w-3" />
            <span>Invited by {invitation.inviter?.name ?? "a team member"}</span>
          </div>

          {invitation.provisioningConfig?.homeProjectId && (
            <div
              className="flex items-center gap-1 mt-1 text-xs"
              style={{ color: MID_GRAY.DEFAULT }}
            >
              <Home className="h-3 w-3" />
              <span>Homepage pre-configured</span>
            </div>
          )}
        </div>
      </div>

      {/* Custom message */}
      {invitation.message && (
        <div
          className="text-xs rounded-md p-2 mb-3 italic"
          style={{
            backgroundColor: BACKGROUND.secondary,
            color: TEXT.secondary,
          }}
        >
          "{invitation.message}"
        </div>
      )}

      {/* Expiry and actions */}
      <div className="flex items-center justify-between gap-2">
        {/* Expiry indicator */}
        <div
          className={cn(
            "flex items-center gap-1 text-xs",
            expiringSoon ? "text-orange-600" : "text-muted-foreground"
          )}
        >
          <Clock className="h-3 w-3" />
          <span>{expiryText}</span>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDecline();
            }}
            disabled={isProcessing}
            className="h-7 px-2 text-xs"
          >
            {isProcessing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <X className="h-3 w-3 mr-1" />
            )}
            Decline
          </Button>

          <Button
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAccept();
            }}
            disabled={isProcessing}
            className="h-7 px-2 text-xs"
            style={{
              backgroundColor: FOREST_GREEN.DEFAULT,
              color: "white",
            }}
          >
            {isProcessing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Check className="h-3 w-3 mr-1" />
            )}
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PendingInvitationsDropdown;
